import { useState } from "react";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { PageShell } from "@/components/site/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { ADDRESS, BRAND_PHOTOS, EMAIL, PHONE, WHATSAPP } from "@/lib/constants";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/public/contact", f);
      setDone(r.data.reference);
      toast.success(`Message sent — reference ${r.data.reference}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell testId="contact-page" overline="Contact" title="Talk to the workshop." image={BRAND_PHOTOS[6]}
      intro="Questions about a quote, a booking or a build? Send a message and a service advisor will reply.">
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-3">
          {[[Phone, "Call", PHONE, `tel:${PHONE}`], [MessageCircle, "WhatsApp", "Message us", WHATSAPP], [Mail, "Email", EMAIL, `mailto:${EMAIL}`], [MapPin, "Workshop", `${ADDRESS}, Zimbabwe`, null]].map(([Icon, l, v, h]) => (
            <div key={l} className="flex gap-4 rounded-2xl bg-white p-5" data-testid={`contact-channel-${l.toLowerCase()}`}>
              <Icon className="h-5 w-5 text-[#F86900] mt-0.5" />
              <div><p className="text-xs uppercase tracking-widest text-[#56585E]">{l}</p>
                {h ? <a href={h} target="_blank" rel="noreferrer" className="font-semibold hover:text-[#F86900]">{v}</a> : <p className="font-semibold">{v}</p>}</div>
            </div>
          ))}
          <p className="text-xs text-[#56585E] px-1">Hours: Mon–Fri 07:30–17:30, Sat 08:00–13:00. Address and hours pending owner verification.</p>
        </div>
        <form onSubmit={submit} className="lg:col-span-3 rounded-2xl bg-white p-6 md:p-8 space-y-4" data-testid="contact-form">
          {done ? (
            <div data-testid="contact-success"><h2 className="font-display text-2xl font-bold">Thanks — we've got it.</h2><p className="mt-2 text-[#56585E]">Your reference is <strong>{done}</strong>. We'll reply by email.</p></div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input required placeholder="Your name" value={f.name} onChange={set("name")} data-testid="contact-name-input" />
                <Input required type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="contact-email-input" />
                <Input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} data-testid="contact-phone-input" />
                <Input placeholder="Subject" value={f.subject} onChange={set("subject")} data-testid="contact-subject-input" />
              </div>
              <Textarea required minLength={5} rows={6} placeholder="How can we help?" value={f.message} onChange={set("message")} data-testid="contact-message-input" />
              <button disabled={busy} data-testid="contact-submit-button" className="rounded-full bg-[#020165] text-white font-semibold px-7 py-3 hover:bg-[#0a0a8a] disabled:opacity-60 transition-colors">
                {busy ? "Sending…" : "Send message"}
              </button>
            </>
          )}
        </form>
      </div>
    </PageShell>
  );
}
